import { Color3, Matrix, Space, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Nullable } from '@babylonjs/core/types';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { KbAxisMorphGizmo } from './axis-morph-gizmo';
import { KbGizmo } from './gizmo';
import { KbPlaneRotationGizmo } from './plane-rotation-gizmo';

const tessellation = 32;

/**
 * TODO
 * Copy in plane rotation gizmo code here and change it to use a sphere and click projection on the sphere
 * Convert it to return an axis
 * Edit code in here to return an axis and angle
 * update gizmo tools code to hook to this gizmo
 * remove the vector option from the original rotation gizmo (now the euler rotation gizmo), simplify code
 */

/**
 * Gizmo that enables rotating a mesh along 3 axis
 */
export class KbAxisAngleGizmo extends KbGizmo {
    public targetAxis = new Vector3();
    /**
     * Internal gizmo used for interactions on the angle around the axis
     */
    public angleGizmo: KbPlaneRotationGizmo | undefined;

    /**
     * Internal gizmo used for interactions modifying the axis
     */
    public axisGizmo: KbAxisMorphGizmo;

    /** Fires an event when any of it's sub gizmos are dragged */
    public onDragStartObservable = new Observable<void>();
    /** Fires an event when any of it's sub gizmos are released from dragging */
    public onDragObservable = new Observable<{ deltaAxis: Vector3; deltaAngle: number }>();
    public onDragEndObservable = new Observable<{ deltaAxis: Vector3; deltaAngle: number }>();

    public gizmoOffset: KbVector | undefined;
    public placementOffset: KbVector | undefined;
    private _positionSpace: TransformNode | undefined;

    attachTo(target: KbVector, positionSpace?: SpaceNode, placementOffset?: KbVector) {
        if (positionSpace) {
            this._positionSpace = this.kbViewer.getRendererNode(positionSpace);
        }

        this._updateGizmoPosition(target, placementOffset);
    }
    updateGizmoPosition(changeTarget?: KbVector, placementOffset?: KbVector) {
        // Need to wait for the next frame because this can be called before the babylon object is actually updated
        this.kbViewer.onNextFrame(() => {
            this._updateGizmoPosition(changeTarget, placementOffset);
        });
    }

    private _updateGizmoPosition(changeTarget?: KbVector, placementOffset?: KbVector) {
        // Maintain the placement offset if it is not provided later, so this can be called from other contexts to update based on external changes
        if (!placementOffset && this.placementOffset) {
            placementOffset = this.placementOffset;
        } else if (placementOffset) {
            this.placementOffset = placementOffset;
        }

        const positionSpaceTransform = this._positionSpace?.computeWorldMatrix(true);
        if (changeTarget) {
            changeTarget.toVec3(this.targetAxis);

            // If target is a vector, then we want to rotate it as a direction, so set the gizmo position to the origin to calculations are done from there
            // But store the position as the offset because we want the gizmo itself to be visually placed on the position space origin
            this.attachMeshAtNodeSpace(
                this.gizmoLayer.utilityLayerScene,
                placementOffset || KbVector.Zero(),
                positionSpaceTransform
            );

            if (this.attachedMesh) {
                this.gizmoOffset = KbVector.FromVec3(this.attachedMesh.position);
                this.attachedMesh.position = Vector3.Zero();
                this.attachedMesh.computeWorldMatrix(true);
            }
            const targetAxisTransformed = this.targetAxis.clone();
            if (positionSpaceTransform) {
                Vector3.TransformCoordinatesToRef(
                    this.targetAxis,
                    positionSpaceTransform.getRotationMatrix(),
                    targetAxisTransformed
                );
            }
            this.axisGizmo.direction.copyFrom(targetAxisTransformed);
        }
        if (this.gizmoOffset && this.attachedMesh) {
            this.attachedMesh.position.copyFrom(this.gizmoOffset.toVec3());
            this.axisGizmo.gizmoPosition.copyFrom(this.gizmoOffset.toVec3());
            // if (placementOffset) {
            //     this.axisGizmo.gizmoPosition.addInPlace(placementOffset.toVec3());
            // }
        }
        // if (positionSpaceTransform) {
        //     Vector3.TransformCoordinatesToRef(this.axisGizmo.gizmoPosition, positionSpaceTransform, this.axisGizmo.gizmoPosition);
        // }
        super.updateGizmoPosition();
    }

    public get attachedMesh() {
        return super.attachedMesh;
    }
    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        super.attachedMesh = mesh;
        if (mesh) {
            const angleMesh = new Mesh('', this.gizmoLayer.utilityLayerScene);
            mesh.addChild(angleMesh);
            if (this.angleGizmo) {
                this.angleGizmo.attachedMesh = angleMesh;
            }
            angleMesh.lookAt(this.targetAxis);
            angleMesh.setPivotPoint(mesh.getPivotPoint(), Space.WORLD);
        } else if (this.angleGizmo) {
            this.angleGizmo.attachedMesh = null;
        }
    }

    private axisChange: Vector3;
    private angleChange: number;
    /**
     * Creates a RotationGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param tessellation Amount of tessellation to be used when creating rotation circles
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer,
        disableAngle = false
    ) {
        super(gizmoLayer);
        this.updateGizmoRotationToMatchAttachedMesh = true;
        this.axisGizmo = new KbAxisMorphGizmo(Color3.Green().scale(0.5), gizmoLayer, tessellation);
        if (!disableAngle) {
            this.angleGizmo = new KbPlaneRotationGizmo(
                new Vector3(0, 0, 1),
                Color3.Green().scale(0.5),
                gizmoLayer,
                tessellation
            );
            this.angleGizmo.updateGizmoPositionToMatchAttachedMesh = true;
        }

        const zeroVector = Vector3.Zero();
        const tempVector = Vector3.Zero();
        const tempVector2 = Vector3.Zero();
        let positionSpaceTransform: Matrix | undefined;
        this.axisChange = Vector3.Zero();
        this.angleChange = 0;

        // Relay drag events
        [this.axisGizmo, this.angleGizmo].forEach(gizmo => {
            if (gizmo) {
                gizmo.dragBehavior.onDragStartObservable.add(() => {
                    this.onDragStartObservable.notifyObservers();
                    this.axisChange = Vector3.Zero();
                    this.angleChange = 0;
                    if (!positionSpaceTransform && this._positionSpace) {
                        positionSpaceTransform = this._positionSpace
                            .computeWorldMatrix(true)
                            .getRotationMatrix()
                            .clone()
                            .invert();
                    }
                });
                gizmo.dragBehavior.onDragEndObservable.add(() => {
                    this.onDragEndObservable.notifyObservers({
                        deltaAxis: this.axisChange,
                        deltaAngle: this.angleChange,
                    });
                });
            }
        });
        this.axisGizmo.updateObservable.add(event => {
            tempVector.copyFrom(this.targetAxis);
            // if (positionSpaceTransform) {
            //     Vector3.TransformCoordinatesToRef(tempVector, positionSpaceTransform, tempVector);
            // }

            tempVector.rotateByQuaternionToRef(event.delta, tempVector2);
            // if (positionSpaceTransform) {
            //     Vector3.TransformCoordinatesToRef(tempVector2, positionSpaceTransform.clone().invert(), tempVector2);
            // }

            tempVector2.subtractToRef(this.targetAxis, tempVector2);

            this.targetAxis.addInPlace(tempVector2);
            this.axisChange.addInPlace(tempVector2);
            this.onDragObservable.notifyObservers({ deltaAxis: tempVector2, deltaAngle: 0 });

            if (this.angleGizmo && this.angleGizmo.attachedMesh) {
                this.angleGizmo.attachedMesh.lookAt(
                    positionSpaceTransform
                        ? Vector3.TransformCoordinates(this.targetAxis, positionSpaceTransform)
                        : this.targetAxis
                );
            }
        });
        if (this.angleGizmo) {
            this.angleGizmo.updateObservable.add(event => {
                this.angleChange -= event.deltaAngle;
                this.onDragObservable.notifyObservers({ deltaAxis: zeroVector, deltaAngle: -event.deltaAngle });
            });
        }

        this.attachedMesh = null;
    }

    /**
     * Drag distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public set snapDistance(value: number) {
        if (this.axisGizmo) {
            this.axisGizmo.snapDistance = value;
        }
        if (this.angleGizmo) {
            this.angleGizmo.snapDistance = value;
        }
    }
    public get snapDistance() {
        return this.axisGizmo.snapDistance;
    }

    /**
     * Ratio for the scale of the gizmo (Default: 1)
     */
    public set scaleRatio(value: number) {
        if (this.axisGizmo) {
            this.axisGizmo.scaleRatio = value;
        }
        if (this.angleGizmo) {
            this.angleGizmo.scaleRatio = value;
        }
    }
    public get scaleRatio() {
        return this.axisGizmo.scaleRatio;
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        if (this.axisGizmo) {
            this.axisGizmo.dispose();
        }
        if (this.angleGizmo) {
            this.angleGizmo.dispose();
        }
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
    }

    /**
     * CustomMeshes are not supported by this gizmo
     * @param mesh The mesh to replace the default mesh of the gizmo
     */
    public setCustomMesh(mesh: Mesh) {
        Logger.Error(
            'Custom meshes are not supported on this gizmo, please set the custom meshes on the gizmos contained within this one (gizmo.xGizmo, gizmo.angleGizmo, gizmo.zGizmo)'
        );
    }
}
